import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, type ChatPresence, type ConversationView } from '../../api/client';
import { useAuth } from '../../lib/auth';
import { Icon } from '../../lib/icons';
import { usePresenceList } from '../../lib/messages';
import { initials } from '../../lib/session';
import { afterHours, apartShort, apartText, dayThereIfDifferent, displayZone, minutesApart, shortZoneName, timeIn, zoneName } from '../../lib/time';
import { Avatar, hueOf } from './Avatar';

/**
 * Coworkers' local time in Messages (1.12.13): "9:14 AM local time (Pacific)" in a direct message,
 * a card with their time, the team clock across the top of a channel, and a line above the message
 * box when it's late where they are. Only coworkers who share their local time (on unless they
 * turn it off), and only ever inside the company: people at other companies never see it.
 */

/** The time now, a new value every `ms` (at the turn of the minute by default), while mounted. */
export function useClock(ms = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let t: number;
    const tick = () => {
      setNow(new Date());
      t = window.setTimeout(tick, ms - (Date.now() % ms) + 50);
    };
    t = window.setTimeout(tick, ms - (Date.now() % ms) + 50);
    return () => window.clearTimeout(t);
  }, [ms]);
  return now;
}

type Member = { id: string; name: string; extension: string | null };
const memberCache = new Map<string, { at: number; people: Promise<Member[]> }>();

/** A conversation's people (shared by the team clock and the late line; looked up again after a minute). */
export function useMembers(domain: string, id: string, on: boolean): Member[] | null {
  const [people, setPeople] = useState<Member[] | null>(null);
  useEffect(() => {
    if (!on) return;
    let live = true;
    const key = `${domain.toLowerCase()} ${id}`;
    let hit = memberCache.get(key);
    if (!hit || Date.now() - hit.at > 60_000) {
      hit = { at: Date.now(), people: api.conversationMembers(domain, id).then((r) => r.people) };
      hit.people.catch(() => memberCache.delete(key));
      memberCache.set(key, hit);
    }
    hit.people.then((p) => live && setPeople(p)).catch(() => live && setPeople(null));
    return () => {
      live = false;
    };
  }, [domain, id, on]);
  return people;
}

/** Coworkers' local time zones in a company (those who share them). */
export function useZones(domain: string): Record<string, string> {
  return usePresenceList(domain)?.zones ?? {};
}

/** "9:14 AM local time (Pacific)": only when their time isn't the same as yours. */
export function localTimeText(zone: string | undefined, now: Date): string {
  if (!zone) return '';
  const here = displayZone();
  if (minutesApart(zone, here) === 0) return '';
  const day = dayThereIfDifferent(zone, here, now);
  return `${timeIn(zone, now)}${day ? ` ${day}` : ''} local time (${shortZoneName(zone)})`;
}

/** Their local time, beside a direct message's name in the list ("9:14 AM"), when it isn't yours. */
export function ListLocalTime({ zone }: { zone: string | undefined }) {
  const now = useClock();
  if (!zone || minutesApart(zone, displayZone()) === 0) return null;
  return (
    <span className="msg-link-time" title={`Their local time (${zoneName(zone)})`}>
      {timeIn(zone, now)}
    </span>
  );
}

/** A coworker's card: how they show, their local time and how far from yours, and their extension. */
export function PersonCard({
  person,
  presence,
  zone,
  now,
  statusLine,
  onClose,
  actions,
  style
}: {
  person: { id: string; name: string; extension?: string | null };
  presence: ChatPresence | undefined;
  zone: string | undefined;
  now: Date;
  statusLine: string;
  onClose: () => void;
  actions?: ReactNode;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Outside the card and what opened it (the name beside it toggles it itself).
    const away = (e: MouseEvent) => {
      const box = ref.current?.parentElement ?? ref.current;
      if (box && !box.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('mousedown', away);
    window.addEventListener('keydown', key);
    ref.current?.focus();
    return () => {
      window.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', key);
    };
  }, [onClose]);
  const here = displayZone();
  const apart = zone ? minutesApart(zone, here) : 0;
  const day = zone ? dayThereIfDifferent(zone, here, now) : null;
  return (
    <div className="person-card" role="dialog" aria-label={person.name} tabIndex={-1} ref={ref} style={style}>
      <div className="person-card-head">
        <Avatar id={person.id} name={person.name} presence={presence} />
        <span className="cell-stack">
          <strong>{person.name}</strong>
          {statusLine && <small className="muted">{statusLine}</small>}
        </span>
      </div>
      <dl className="person-card-facts">
        {zone && (
          <>
            <dt>Local time</dt>
            <dd>
              <strong>
                {timeIn(zone, now)}
                {day ? ` ${day}` : ''}
              </strong>{' '}
              · {zoneName(zone)}
              {afterHours(zone, now) && <span className="person-card-late"> · outside their day</span>}
            </dd>
            <dt>From you</dt>
            <dd>{apartText(apart)}</dd>
          </>
        )}
        {person.extension && (
          <>
            <dt>Extension</dt>
            <dd>{person.extension}</dd>
          </>
        )}
      </dl>
      {actions && <div className="person-card-actions">{actions}</div>}
    </div>
  );
}

const SHOWN: Record<ChatPresence['status'], string> = { online: 'Online', idle: 'Idle', dnd: 'Do not disturb', offline: 'Offline' };

/** A coworker's name on a message: opens their card (how they show, their local time, how far from yours). */
export function AuthorName({ domain, id, name }: { domain: string; id: string; name: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="msg-author-wrap">
      <button type="button" className="msg-author msg-author-btn" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((o) => !o)}>
        {name}
      </button>
      {open && <AuthorCard domain={domain} id={id} name={name} onClose={() => setOpen(false)} />}
    </span>
  );
}

function AuthorCard({ domain, id, name, onClose }: { domain: string; id: string; name: string; onClose: () => void }) {
  const list = usePresenceList(domain);
  const now = useClock();
  const pr = list?.people[id];
  return <PersonCard person={{ id, name }} presence={pr} zone={list?.zones?.[id]} now={now} statusLine={pr ? SHOWN[pr.status] : ''} onClose={onClose} />;
}

// --- the line above the message box when it's late for them --------------------------------------------

const hidden = new Set<string>();
const hideKey = (id: string) => `stitch.late-hidden.${id}`;

/**
 * "It's 12:14 AM Thursday for Priya (Philippine Time)." above the message box, when it's before
 * 8 AM or after 6 PM where they are. In a channel or a group, who's outside their day. Hidden for
 * a conversation until Stitch is opened again, once dismissed.
 */
export function LateLine({ domain, c, myId }: { domain: string; c: ConversationView; myId: string }) {
  const zones = useZones(domain);
  const now = useClock();
  const members = useMembers(domain, c.id, c.kind === 'channel');
  const [gone, setGone] = useState(() => {
    try {
      return hidden.has(c.id) || sessionStorage.getItem(hideKey(c.id)) === '1';
    } catch {
      return hidden.has(c.id);
    }
  });
  useEffect(() => {
    try {
      setGone(hidden.has(c.id) || sessionStorage.getItem(hideKey(c.id)) === '1');
    } catch {
      setGone(hidden.has(c.id));
    }
  }, [c.id]);
  if (gone || c.kind === 'text') return null;
  const here = displayZone();
  // Only people whose time isn't yours: you know when it's late where you are.
  const people = (c.kind === 'channel' ? (members ?? []) : c.people).filter((p) => p.id !== myId && zones[p.id] && minutesApart(zones[p.id]!, here) !== 0 && afterHours(zones[p.id]!, now));
  if (!people.length) return null;
  const dm = c.kind === 'dm' && c.people.length === 2;
  let text: ReactNode;
  if (dm) {
    const p = people[0]!;
    const z = zones[p.id]!;
    const day = dayThereIfDifferent(z, here, now);
    text = (
      <>
        It’s{' '}
        <b>
          {timeIn(z, now)}
          {day ? ` ${longDay(z, now)}` : ''}
        </b>{' '}
        for {p.name} ({zoneName(z)}).
      </>
    );
  } else {
    const named = people.slice(0, 2);
    const more = people.length - named.length;
    text = (
      <>
        It’s night for {people.length === 1 ? '1 person' : `${people.length} people`} here:{' '}
        {named.map((p, i) => (
          <span key={p.id}>
            {i > 0 ? (i === named.length - 1 && !more ? ' and ' : ', ') : ''}
            {p.name} (<b>{timeIn(zones[p.id]!, now)}</b>)
          </span>
        ))}
        {more > 0 ? ` and ${more} more` : ''}.
      </>
    );
  }
  return (
    <div className="late-line" role="note">
      <Icon name="moon" size={16} />
      <span className="grow">{text}</span>
      <button
        type="button"
        className="icon-btn small"
        aria-label={dm ? `Hide this for ${people[0]!.name.split(' ')[0]}` : c.kind === 'channel' ? `Hide this in #${c.title}` : 'Hide this here'}
        onClick={() => {
          hidden.add(c.id);
          try {
            sessionStorage.setItem(hideKey(c.id), '1');
          } catch {
            // Hidden until Stitch reloads.
          }
          setGone(true);
        }}
      >
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

/**
 * A strip that moves only when what's in it doesn't fit: `windowRef` on the visible part, `runRef`
 * on the first run of items (a second, hidden run follows it while it moves, for a seamless loop).
 */
export function useMarquee<W extends HTMLElement, R extends HTMLElement>(deps: unknown[]) {
  const windowRef = useRef<W>(null);
  const runRef = useRef<R>(null);
  const [fits, setFits] = useState(true);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const w = windowRef.current;
    const r = runRef.current;
    if (!w || !r) return;
    const check = () => {
      setFits(r.scrollWidth <= w.clientWidth + 1);
      setWidth(r.scrollWidth);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(w);
    ro.observe(r);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { windowRef, runRef, fits, width };
}

/** The device asks for less motion. */
export function useLessMotion(): boolean {
  const [less, setLess] = useState(() => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    let q: MediaQueryList;
    try {
      q = window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch {
      return;
    }
    const on = () => setLess(q.matches);
    q.addEventListener('change', on);
    return () => q.removeEventListener('change', on);
  }, []);
  return less;
}

function longDay(zone: string, now: Date): string {
  try {
    return new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: zone }).format(now);
  } catch {
    return '';
  }
}

// --- the team clock across the top of a channel ----------------------------------------------------------

interface Stop {
  zone: string;
  apart: number;
  people: { id: string; name: string }[];
}

/**
 * The team clock (1.12.13): your time pinned at the left, then one stop per time zone the channel's
 * people are in (time, ahead or behind you, their faces, a moon after hours). It moves only when it
 * doesn't fit; it stops while you point at it or tab into it, has Pause, and stays still with less
 * motion or "Keep it still". Not shown when everyone's time is yours, or when you turned it off.
 */
export function TeamClock({ domain, c, myId }: { domain: string; c: ConversationView; myId: string }) {
  const { state } = useAuth();
  const zones = useZones(domain);
  const presence = usePresenceList(domain);
  const now = useClock();
  const navigate = useNavigate();
  const [open, setOpen] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const on = state?.user?.teamClock !== false && c.kind === 'channel';
  const still = state?.user?.teamClockStill === true;
  const members = useMembers(domain, c.id, on);

  const here = displayZone();
  const stops = useMemo<Stop[]>(() => {
    const by = new Map<string, Stop>();
    for (const p of members ?? []) {
      if (p.id === myId) continue;
      const z = zones[p.id];
      if (!z) continue;
      const apart = minutesApart(z, here);
      if (apart === 0) continue;
      // Zones with the same time now are one stop (named after the first).
      const key = String(apart);
      const s = by.get(key) ?? { zone: z, apart, people: [] };
      s.people.push({ id: p.id, name: p.name });
      by.set(key, s);
    }
    return [...by.values()].sort((a, b) => a.apart - b.apart);
  }, [members, zones, myId, here]);

  const { windowRef, runRef, fits, width } = useMarquee<HTMLDivElement, HTMLSpanElement>([stops.length, on]);
  const lessMotion = useLessMotion();

  if (!on || !stops.length) return null;
  const marquee = !fits && !still && !lessMotion;
  const stopEls = (copy: boolean) =>
    stops.map((s) => {
      const late = afterHours(s.zone, now);
      const day = dayThereIfDifferent(s.zone, here, now);
      const label = `${zoneName(s.zone)}, ${timeIn(s.zone, now)}${day ? ` ${longDay(s.zone, now)}` : ''}, ${apartText(s.apart)}${late ? ', outside working hours' : ''}: ${s.people.map((p) => p.name).join(', ')}`;
      return (
        <span key={`${copy ? 'b' : 'a'}${s.apart}`} className="tk-item">
          <button type="button" className={`tk-stop${late ? ' tk-late' : ''}`} aria-label={copy ? undefined : label} tabIndex={copy ? -1 : 0} aria-hidden={copy || undefined} onClick={() => setOpen(open === s.zone ? null : s.zone)}>
            <span className="tk-zone">{shortZoneName(s.zone)}</span>
            {late && <Icon name="moon" size={14} />}
            <span className="tk-time">{timeIn(s.zone, now).replace(/ ?(AM|PM)$/, '')}<span className="tk-ampm">{/PM$/.test(timeIn(s.zone, now)) ? 'PM' : 'AM'}</span></span>
            {day && <span className="tk-day">{day}</span>}
            <span className="tk-delta">
              <span aria-hidden="true">{s.apart > 0 ? '▲' : '▼'}</span>
              {apartShort(s.apart)}
            </span>
            <span className="tk-faces">
              {s.people.slice(0, 3).map((p) => (
                <span key={p.id} className="tk-face" style={{ '--msg-hue': hueOf(p.id) } as CSSProperties}>
                  {initials(p.name)}
                </span>
              ))}
              {s.people.length > 3 && <span className="tk-face tk-more">+{s.people.length - 3}</span>}
            </span>
          </button>
          <span className="tk-sep" aria-hidden="true" />
        </span>
      );
    });
  const openStop = stops.find((s) => s.zone === open);
  return (
    <div className="tk-wrap">
      <div className={`tk${marquee ? ' tk-marquee' : ''}${marquee && paused ? ' tk-paused' : ''}${!fits && !marquee ? ' tk-scrolls' : ''}`} role="region" aria-label={`Team clock for #${c.title}`}>
        <div className="tk-you">
          <span className="tk-you-tag">YOU</span>
          <span className="tk-time">
            {timeIn(here, now).replace(/ ?(AM|PM)$/, '')}
            <span className="tk-ampm">{/PM$/.test(timeIn(here, now)) ? 'PM' : 'AM'}</span>
          </span>
          <span className="tk-zone tk-you-zone">{shortZoneName(here)}</span>
        </div>
        <div className="tk-window" ref={windowRef}>
          <div className="tk-track" style={marquee ? ({ '--tk-run': `${Math.max(20, Math.round(width / 40))}s` } as CSSProperties) : undefined}>
            <span className="tk-run" ref={runRef}>
              {stopEls(false)}
            </span>
            {marquee && (
              <span className="tk-run" aria-hidden="true">
                {stopEls(true)}
              </span>
            )}
          </div>
        </div>
        {marquee && (
          <div className="tk-ctl">
            <button type="button" className="tk-btn" aria-label={paused ? 'Play the team clock' : 'Pause the team clock'} aria-pressed={paused} onClick={() => setPaused(!paused)}>
              <Icon name={paused ? 'play' : 'pause'} size={15} />
            </button>
          </div>
        )}
      </div>
      {openStop && (
        <PersonList
          stop={openStop}
          now={now}
          presence={presence?.people}
          members={members ?? []}
          onClose={() => setOpen(null)}
          onMessage={async (id) => {
            const dm = await api.startDm(domain, [id]);
            setOpen(null);
            navigate(`/t/${encodeURIComponent(domain)}/messages/${dm.conversation.id}`);
          }}
        />
      )}
    </div>
  );
}

/** Who's in a time zone, from the team clock: their status and Message. */
function PersonList({
  stop,
  now,
  presence,
  members,
  onClose,
  onMessage
}: {
  stop: Stop;
  now: Date;
  presence: Record<string, ChatPresence> | undefined;
  members: { id: string; name: string; extension: string | null }[];
  onClose: () => void;
  onMessage: (id: string) => Promise<void>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const away = (e: MouseEvent) => {
      const box = ref.current?.parentElement ?? ref.current;
      if (box && !box.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('mousedown', away);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', key);
    };
  }, [onClose]);
  const here = displayZone();
  const day = dayThereIfDifferent(stop.zone, here, now);
  const shown = SHOWN;
  return (
    <div className="person-card tk-card" role="dialog" aria-label={zoneName(stop.zone)} ref={ref}>
      <span className="eyebrow">{zoneName(stop.zone)}</span>
      <span>
        <strong className="tk-card-time">
          {timeIn(stop.zone, now)}
          {day ? ` ${day}` : ''}
        </strong>{' '}
        · {apartText(stop.apart)}
      </span>
      <ul className="plain-list">
        {stop.people.map((p) => {
          const pr = presence?.[p.id];
          const ext = members.find((m) => m.id === p.id)?.extension;
          return (
            <li key={p.id} className="tk-card-person">
              <Avatar id={p.id} name={p.name} small presence={pr} />
              <span className="cell-stack grow">
                <strong>{p.name}</strong>
                <small className="muted">{[pr ? shown[pr.status] : '', ext ? `Ext. ${ext}` : ''].filter(Boolean).join(' · ')}</small>
              </span>
              <button type="button" className="btn secondary small" onClick={() => void onMessage(p.id)}>
                Message
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
